import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCamp, logoUrl } from "@/context/SettingsContext";
import { useStats } from "@/context/StatsContext";
import { APP_NAME } from "@/lib/app";
import { buildMenu, defaultLayout, resolveLayout } from "@/lib/menuCatalog";

export default function Sidebar({ collapsed, setCollapsed }) {
  const { t, lang } = useLang();
  const { camp } = useCamp();
  const { user } = useAuth();
  const { updatedAt, stats } = useStats();
  const location = useLocation();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(0);
  const [unread, setUnread] = useState(0);
  const [menuCfg, setMenuCfg] = useState(null);
  const isAdmin = ["admin", "superadmin"].includes(user?.role);
  const fin = Number(user?.finance_class || 0);
  const maySee = (it) => (!it.admin || isAdmin) && (!it.fin || isAdmin || fin >= it.fin) && (!it.mailbox || isAdmin || user?.mailbox_access);

  useEffect(() => {
    api.get("/menu-config").then(({ data }) => setMenuCfg(data)).catch(() => {});
  }, []);

  const layout = user?.role === "superadmin" ? defaultLayout("admin") : resolveLayout(user?.role, menuCfg);
  const menu = buildMenu(layout, maySee, lang);

  // Stip bij Verhuur: te laat terug of waarborg nog niet ontvangen
  useEffect(() => {
    const tick = () => {
      api.get("/rentals/alerts").then(({ data }) => setAlerts(data.total || 0)).catch(() => {});
      api.get("/mailbox/unread").then(({ data }) => setUnread(data.unseen || 0)).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [location.pathname, updatedAt]);

  return (
    <motion.aside
      animate={{ width: collapsed ? 76 : 258 }}
      transition={{ duration: 0.25, ease: "easeInOut" }}
      className="hidden md:flex flex-col bg-[hsl(var(--sidebar))] border-r border-border h-screen sticky top-0 shrink-0 z-30"
      data-testid="sidebar"
    >
      <div className={`flex items-center gap-3 h-16 px-4 border-b border-border ${collapsed ? "justify-center" : ""}`}>
        {collapsed ? (
          <img src={logoUrl(camp, "tent")} alt={camp.camp_name || APP_NAME} className="h-12 w-auto shrink-0" data-testid="sidebar-logo-collapsed" />
        ) : (
          <img src={logoUrl(camp, "main")} alt={camp.camp_name || APP_NAME} className="h-11 w-auto shrink-0" data-testid="sidebar-logo" />
        )}
        {!collapsed && (
          <div className="overflow-hidden">
            <p className="font-display font-extrabold leading-none text-foreground truncate">{camp.camp_name || APP_NAME}</p>
            <p className="text-[11px] text-muted-foreground mt-1 truncate">{t("tagline")}</p>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto cb-scroll py-4 px-3 space-y-5">
        {menu.map((g) => (
          <div key={g.id} className="space-y-0.5" data-testid={`nav-group-${g.id}`}>
            {collapsed
              ? <div className="mx-auto mb-2 h-px w-7 bg-border" />
              : <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/80">{g.label}</p>}
            {g.items.map((it) => {
              const Icon = it.icon;
              const active = location.pathname === it.path || (it.path !== "/" && location.pathname.startsWith(`${it.path}/`));
              return (
                <NavLink
                  key={it.path}
                  to={it.path}
                  title={t(it.key)}
                  data-testid={`nav-${it.path === "/" ? "dashboard" : it.path.slice(1)}`}
                  className={`group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 touch-manipulation ${
                    active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  {active && <span className="absolute left-0 top-1/2 h-6 -translate-y-1/2 w-[3px] rounded-r-full bg-accent-foreground" />}
                  <span className="relative shrink-0">
                    <Icon className="h-[18px] w-[18px]" />
                    {it.path === "/verhuur" && alerts > 0 && (
                      <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-[hsl(var(--sidebar))] animate-pulse" data-testid="nav-rental-dot" />
                    )}
                    {it.path === "/mailbox" && unread > 0 && (
                      <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold leading-4 text-center ring-2 ring-[hsl(var(--sidebar))]" data-testid="nav-mailbox-badge">{unread > 99 ? "99+" : unread}</span>
                    )}
                    {it.path === "/aankomsten" && (stats?.arriving_late || 0) > 0 && collapsed && (
                      <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-[hsl(var(--sidebar))] animate-pulse" title={`${stats.arriving_late} ${t("overdue_label").toLowerCase()}`} data-testid="nav-aankomsten-dot" />
                    )}
                  </span>
                  {!collapsed && <span className="truncate">{t(it.key)}</span>}
                  {it.path === "/verhuur" && alerts > 0 && !collapsed && (
                    <span className="ml-auto rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white tabular-nums" data-testid="nav-rental-badge">{alerts}</span>
                  )}
                  {it.path === "/aankomsten" && (stats?.arriving_late || 0) > 0 && !collapsed && (
                    <span className="ml-auto rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white tabular-nums" title={`${stats.arriving_late} ${t("overdue_label").toLowerCase()}`} data-testid="nav-aankomsten-badge">{stats.arriving_late}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-border">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center gap-3 rounded-xl px-3 py-2 text-xs text-muted-foreground hover:bg-muted transition-colors"
          data-testid="sidebar-toggle"
        >
          <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          {!collapsed && <span>{t("collapse")}</span>}
        </button>
      </div>
    </motion.aside>
  );
}
